import type { StreamPlayerApi } from '@cloudflare/stream-react'
import { useMediaQuery } from '@mantine/hooks'
import { AnimatePresence, motion } from 'motion/react'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link } from 'react-router'
import { withTrailingSlash } from 'ufo'
import TablerChevronLeft from '~icons/tabler/chevron-left'
import TablerChevronRight from '~icons/tabler/chevron-right'
import { useBannerCarousel } from 'src/components/BannerCarousel/useBannerCarousel'
import { useMyInfo } from 'src/services/apollo/auth'
import { CampaignMediaType } from 'src/services/apollo/generated/graphql'
import { useMyMembership } from 'src/services/apollo/membership'
import type { CampaignAdEntry } from 'src/services/orpc/campaignAds'
import { cx } from 'src/utils/cx'
import { useIsLg } from 'src/utils/hooks/useBreakpoint'
import { startEventGroup, track } from 'src/utils/tracker'
import { openSignModal } from '../Account/utils'
import { MediaStreamPlayer } from '../Media/MediaStreamPlayer'
import { AdLabel, AdMedia } from './AdUnitCommon'
import { trackCampaignClick } from './campaign'
import {
  checkAdUnitConditionWithMembership,
  useCampaignViewTracker,
} from './hooks'

const CAROUSEL_TRANSITION_DURATION = 300

interface FeedAdSlide {
  media: string
  type: CampaignMediaType
  title?: string | null
}

type Playable = Pick<
  HTMLVideoElement,
  'play' | 'pause' | 'currentTime' | 'paused'
>

interface VideoPlayback {
  registerVideoRef: (renderPos: number, el: HTMLVideoElement | null) => void
  getStreamRef: (renderPos: number) => {
    current: StreamPlayerApi | undefined
  }
  onSlideEnded: (renderPos: number) => void
  onSlidePlay: (renderPos: number) => void
}

// Active slide plays; non-active is paused without seeking, so revisits
// resume from where they stopped. Only `ended` rewinds to 0.
function useFeedAdVideoPlayback({
  currentIndex,
  visibleSlides,
  next,
}: {
  currentIndex: number
  visibleSlides: readonly FeedAdSlide[]
  next: () => void
}): VideoPlayback {
  const total = visibleSlides.length

  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map())
  const registerVideoRef = useCallback(
    (pos: number, el: HTMLVideoElement | null) => {
      if (el) videoRefs.current.set(pos, el)
      else videoRefs.current.delete(pos)
    },
    [],
  )

  // Cloudflare Stream is in a cross-origin iframe, so we go through its
  // streamRef API instead of the wrapper-div querySelector.
  const streamRefByPos = useRef<
    Map<number, { current: StreamPlayerApi | undefined }>
  >(new Map())
  const getStreamRef = useCallback((pos: number) => {
    let ref = streamRefByPos.current.get(pos)
    if (!ref) {
      ref = { current: undefined }
      streamRefByPos.current.set(pos, ref)
    }
    return ref
  }, [])

  const getPlayable = useCallback(
    (pos: number): Playable | undefined =>
      videoRefs.current.get(pos) ?? streamRefByPos.current.get(pos)?.current,
    [],
  )
  const forEachPlayable = useCallback(
    (cb: (p: Playable, pos: number) => void) => {
      const seen = new Set<number>()
      videoRefs.current.forEach((el, pos) => {
        seen.add(pos)
        cb(el, pos)
      })
      streamRefByPos.current.forEach((ref, pos) => {
        if (seen.has(pos)) return
        if (ref.current) cb(ref.current, pos)
      })
    },
    [],
  )

  // Read currentIndex via ref so per-render handlers see the latest.
  const currentIndexRef = useRef(currentIndex)
  currentIndexRef.current = currentIndex

  const onSlideEnded = useCallback(
    (pos: number) => {
      const p = getPlayable(pos)
      if (p) p.currentTime = 0
      if (pos === currentIndexRef.current + 1) next()
    },
    [next, getPlayable],
  )
  // Catches hls.js's auto-play on offscreen slides; paused without
  // seeking to keep the resume position.
  const onSlidePlay = useCallback(
    (pos: number) => {
      if (pos === currentIndexRef.current + 1) return
      const p = getPlayable(pos)
      if (!p || p.paused) return
      p.pause()
    },
    [getPlayable],
  )

  useLayoutEffect(() => {
    if (total === 0) return
    const activePos = currentIndex + 1
    forEachPlayable((p, pos) => {
      if (pos === activePos) return
      if (!p.paused) p.pause()
    })
  }, [currentIndex, visibleSlides, total, forEachPlayable])
  // Runs after children's effects (e.g. hls.js attachMedia) so play()
  // has a media source.
  useEffect(() => {
    if (total === 0) return
    const p = getPlayable(currentIndex + 1)
    if (!p) return
    p.play().catch(() => {})
  }, [currentIndex, visibleSlides, total, getPlayable])

  return { registerVideoRef, getStreamRef, onSlideEnded, onSlidePlay }
}

function FeedAdVideoSlide({
  slide,
  position,
  playback,
  onError,
}: {
  slide: FeedAdSlide
  position: number
  playback: VideoPlayback
  onError: () => void
}) {
  const events = {
    loop: false,
    onEnded: () => playback.onSlideEnded(position),
    onPlay: () => playback.onSlidePlay(position),
    onError,
  }
  return (
    <div
      className="size-full"
      ref={el => {
        const video = el?.querySelector<HTMLVideoElement>('video')
        playback.registerVideoRef(position, video ?? null)
      }}
    >
      <MediaStreamPlayer
        mediaType={slide.type as Exclude<CampaignMediaType, 'IMAGE'>}
        mediaSpec={slide.media}
        className="w-full h-full object-cover"
        videoMediaProps={{ className: 'object-cover', ...events }}
        hlsStreamProps={{
          className: 'pointer-events-none',
          ...events,
          // hls.js swallows fatal errors; without this the
          // carousel would wait on `ended` forever.
          onFatalError: onError,
        }}
        streamProps={{
          className: 'pointer-events-none',
          streamRef: playback.getStreamRef(position),
          ...events,
        }}
      />
    </div>
  )
}

function FeedAdCarousel({
  slides,
  onIndexChange,
}: {
  slides: FeedAdSlide[]
  onIndexChange?: (index: number) => void
}) {
  const [erroredMedia, setErroredMedia] = useState<Set<string>>(() => new Set())
  const handleVideoError = useCallback((media: string) => {
    setErroredMedia(prev => {
      if (prev.has(media)) return prev
      const nextSet = new Set(prev)
      nextSet.add(media)
      return nextSet
    })
  }, [])
  // originalIndex is preserved so onIndexChange can report against the
  // parent's unfiltered `entries` array.
  const visibleSlides = useMemo(
    () =>
      slides
        .map((slide, originalIndex) => ({ ...slide, originalIndex }))
        .filter(s => !erroredMedia.has(s.media)),
    [slides, erroredMedia],
  )
  const total = visibleSlides.length
  const hoveringRef = useRef(false)
  const [hovering, setHovering] = useState(false)
  const reducedMotion =
    useMediaQuery('(prefers-reduced-motion: reduce)') ?? false

  // Video slides advance on `ended` instead of the autoplay timer.
  const skipAutoplayPerSlide = useMemo(
    () => visibleSlides.map(s => s.type !== CampaignMediaType.Image),
    [visibleSlides],
  )

  const {
    currentIndex,
    renderIndex,
    isTransitioning,
    isDragging,
    dragOffset,
    trackRef,
    dragHandlers,
    prev,
    next,
    pause,
    resume,
    notifyManualInteraction,
    goTo,
  } = useBannerCarousel({
    total,
    autoplayInterval: 4000,
    firstSlideInterval: 5000,
    transitionDuration: CAROUSEL_TRANSITION_DURATION,
    autoplay: !reducedMotion,
    reducedMotion,
    hoveringRef,
    skipAutoplayPerSlide,
  })

  const playback = useFeedAdVideoPlayback({
    currentIndex,
    visibleSlides,
    next,
  })

  // Report the parent's `entries` index, not the filtered one.
  useEffect(() => {
    const originalIndex = visibleSlides[currentIndex]?.originalIndex
    if (originalIndex !== undefined) onIndexChange?.(originalIndex)
  }, [currentIndex, visibleSlides, onIndexChange])

  // Native listeners needed: React synthetic pointerdown delegates at document
  // root, firing after the native event has already bubbled through <Link>.
  const outerRef = useRef<HTMLDivElement | null>(null)
  const trackElRef = useRef<HTMLDivElement | null>(null)
  const setTrackRef = useCallback(
    (el: HTMLDivElement | null) => {
      trackRef(el)
      trackElRef.current = el
    },
    [trackRef],
  )
  const dragHandlersRef = useRef(dragHandlers)
  dragHandlersRef.current = dragHandlers

  useEffect(() => {
    const outer = outerRef.current
    const track = trackElRef.current
    if (!outer || !track) return

    const onOuterPointerDown = (e: PointerEvent) => e.stopPropagation()
    const onTrackPointerDown = (e: PointerEvent) => {
      dragHandlersRef.current.onPointerDown(
        e as unknown as ReactPointerEvent<HTMLDivElement>,
      )
    }

    outer.addEventListener('pointerdown', onOuterPointerDown)
    track.addEventListener('pointerdown', onTrackPointerDown)
    return () => {
      outer.removeEventListener('pointerdown', onOuterPointerDown)
      track.removeEventListener('pointerdown', onTrackPointerDown)
    }
  }, [])

  const renderSlides = useMemo(
    () =>
      total === 0
        ? []
        : [visibleSlides.at(-1)!, ...visibleSlides, visibleSlides[0]!],
    [visibleSlides, total],
  )

  if (total === 0) return null

  const totalWithClones = total + 2
  const slidePercent = 100 / totalWithClones

  const trackStyle: CSSProperties = {
    display: 'flex',
    width: `${totalWithClones * 100}%`,
    height: '100%',
    transform: `translateX(calc(-${renderIndex * slidePercent}% + ${dragOffset}px))`,
    transition:
      isTransitioning && !isDragging && !reducedMotion
        ? `transform ${CAROUSEL_TRANSITION_DURATION}ms cubic-bezier(0.25, 0.1, 0.25, 1)`
        : 'none',
  }

  const arrowClass = cx(
    'absolute top-1/2 -translate-y-1/2 z-10',
    'w-6 h-6 rounded-full bg-black/40 hover:bg-black/60',
    'flex items-center justify-center text-white transition-opacity duration-200',
    hovering ? 'opacity-100' : 'opacity-0 pointer-events-none',
  )

  const onInteract = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault()
    e.stopPropagation()
    notifyManualInteraction()
    action()
  }

  return (
    <div
      ref={outerRef}
      className="relative w-full h-full overflow-hidden select-none"
      onMouseEnter={() => {
        hoveringRef.current = true
        setHovering(true)
        pause()
      }}
      onMouseLeave={() => {
        hoveringRef.current = false
        setHovering(false)
        resume()
      }}
    >
      <div
        ref={setTrackRef}
        style={trackStyle}
        className={cx(isDragging && 'cursor-grabbing', 'touch-pan-y')}
      >
        {renderSlides.map((slide, i) => {
          // Clones get their own key prefix so refs don't get reused
          // between a clone and its real slide on error-driven shifts.
          const clone =
            i === 0 ? 'pre' : i === renderSlides.length - 1 ? 'post' : null
          const key = clone ? `${clone}-${slide.media}` : slide.media
          return (
            <div
              key={key}
              className="flex-shrink-0 h-full"
              style={{ width: `${slidePercent}%` }}
            >
              {slide.type === CampaignMediaType.Image ? (
                <img
                  src={slide.media}
                  className="w-full h-full object-cover pointer-events-none"
                  draggable={false}
                  alt={slide.title ?? ''}
                />
              ) : (
                <FeedAdVideoSlide
                  slide={slide}
                  position={i}
                  playback={playback}
                  onError={() => handleVideoError(slide.media)}
                />
              )}
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className={cx(arrowClass, 'left-1')}
        aria-label="Previous slide"
        onClick={e => onInteract(e, prev)}
      >
        <TablerChevronLeft className="size-3.5" />
      </button>
      <button
        type="button"
        className={cx(arrowClass, 'right-1')}
        aria-label="Next slide"
        onClick={e => onInteract(e, next)}
      >
        <TablerChevronRight className="size-3.5" />
      </button>

      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-10">
        {Array.from({ length: total }, (_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            className={cx(
              'rounded-full transition-all duration-300',
              i === currentIndex
                ? 'w-3 h-1.5 bg-white'
                : 'w-1.5 h-1.5 bg-white/60 hover:bg-white/90',
            )}
            onClick={e => onInteract(e, () => goTo(i))}
          />
        ))}
      </div>
    </div>
  )
}

export interface ArtworkFeedAdUnitProps {
  ad: CampaignAdEntry
  position?: number
  // The position of the ad unit in feed when there is 4 columns
  lgPosition?: number
  /**
   * Layout mode for the ad unit
   * - 'grid': CSS grid layout with position classes (default)
   * - 'masonry': Masonry/waterfall layout without position classes
   */
  mode?: 'grid' | 'masonry'
  /** Multiple entries render a carousel, each with its own link/title/description */
  entries?: CampaignAdEntry[]
}

const columnClassNames = [
  'col-start-1',
  'col-start-2',
  'col-start-3',
  'col-start-4',
]
const lgColumnClassNames = [
  'lg:col-start-1',
  'lg:col-start-2',
  'lg:col-start-3',
  'lg:col-start-4',
]

export function ArtworkFeedAdUnit(props: ArtworkFeedAdUnitProps) {
  const { ad, mode = 'grid', entries } = props
  const isMasonry = mode === 'masonry'
  const isLg = useIsLg()
  const { data: userInfo } = useMyInfo()
  const [activeIndex, setActiveIndex] = useState(0)
  const activeAd = entries?.[activeIndex] ?? ad

  const normalizedLink = new URL(activeAd.targetUrl ?? '#', location.origin)
  const isInternalLink = normalizedLink.origin === location.origin
  const isBlogLink = withTrailingSlash(normalizedLink.pathname).startsWith(
    '/articles/',
  )
  const isMembershipLink =
    isInternalLink && normalizedLink.pathname === '/membership/plans'

  const viewTrackingParams = {
    category: 'main_feed',
    scene: 'home',
    position: isMasonry ? undefined : isLg ? props.lgPosition : props.position,
    tracking_id: ad.trackingId,
  }
  const containerRef = useCampaignViewTracker(viewTrackingParams)

  return (
    <div
      ref={containerRef}
      className={cx(
        'w-full group relative isolate p-0.5 @container',
        // Grid mode: add position classes
        !isMasonry && 'row-start-1 h-full',
        !isMasonry &&
          props.position != null &&
          columnClassNames[props.position],
        !isMasonry && (props.position ?? 0) >= 2 && 'max-lg:hidden',
        !isMasonry &&
          props.lgPosition != null &&
          lgColumnClassNames[props.lgPosition],
      )}
    >
      <div
        className={cx(
          'z-[-1] absolute bg-gradient-member transition ease-out duration-300 group-hover:shadow-xl',
          // Normal-sized border
          '-inset-0.5 rounded-[0.625rem]',
          // Smaller border for mobile view
          'max-mmd:inset-0 max-mmd:rounded-lg',
        )}
      />
      <Link
        className={cx(
          'block bg-background-secondary w-full rounded-md overflow-hidden hover:no-underline',
          !isMasonry && 'h-full',
        )}
        to={activeAd.targetUrl ?? '#'}
        target={isInternalLink && !isBlogLink ? undefined : '_blank'}
        draggable={false}
        onClick={event => {
          if (!userInfo && !activeAd.isClickableForNonLoginUser) {
            openSignModal(undefined, { fromPath: 'campaign' })
            event.preventDefault()
            return
          }
          if (isMembershipLink) {
            const trackingLocation = 'homepage-in-feed-ad-unit'
            track('enter_member_click', {
              location: trackingLocation,
            })
            startEventGroup('member_source', trackingLocation, {
              persistent: true,
            })
          }
          trackCampaignClick({
            ...viewTrackingParams,
            tracking_id: activeAd.trackingId,
          })
          if (isInternalLink) {
            setTimeout(() => {
              window.scrollTo(0, 0)
            }, 100)
          }
        }}
      >
        <div
          className={cx(
            'relative w-full aspect-square bg-active overflow-hidden',
            isMasonry && 'rounded-t-md',
          )}
        >
          {entries && entries.length > 1 ? (
            <FeedAdCarousel
              slides={entries.map(e => ({
                media: e.media,
                type: e.type,
                title: e.title,
              }))}
              onIndexChange={setActiveIndex}
            />
          ) : (
            <AdMedia
              className="w-full aspect-square"
              mediaType={activeAd.type}
              mediaSpec={activeAd.media}
              mediaAlt={activeAd.title}
            />
          )}
          {activeAd.isAd ? (
            <AdLabel
              className={cx(
                'absolute bottom-2 right-2',
                !isMasonry && 'md:hidden',
              )}
            />
          ) : null}
        </div>
        <div className="relative flex items-center gap-1.5 w-full h-14 pl-2 pb-0.5">
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={activeIndex}
              className="flex flex-col items-stretch gap-0.5 min-w-0 px-1 py-1.5"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <div className="text-left min-h-[1rem] text-sm font-semibold truncate">
                <span title={activeAd.title}>{activeAd.title}</span>
              </div>
              <div className="text-left w-auto min-h-[1rem] text-xs text-primary-light truncate">
                {activeAd.description}
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="flex-1" />
          {activeAd.isAd ? (
            <AdLabel className="flex-shrink-0 mr-4 max-md:hidden" />
          ) : null}
        </div>
      </Link>
    </div>
  )
}

const calculateFeedAdPosition = (
  ads: CampaignAdEntry[],
  determineAdCondition: (ad: CampaignAdEntry) => boolean,
) => {
  if (ads.length === 2) {
    return ads
      .map((ad, index) => ({ ad, position: index, lgPosition: [0, 3][index] }))
      .filter(({ ad }) => determineAdCondition(ad))
  }
  return ads
    .filter(ad => determineAdCondition(ad))
    .slice(0, 4)
    .map((ad, index) => ({ ad, position: index, lgPosition: index }))
}

function ArtworkFeedCampaignAdUnits({
  feedCampaign,
}: {
  feedCampaign: CampaignAdEntry[]
}) {
  const { data: { subscription } = {} } = useMyMembership()

  const eligible = feedCampaign.filter(ad =>
    checkAdUnitConditionWithMembership(ad, subscription),
  )

  if (eligible.length === 0) return null

  // New behavior: all ads have position → group by position, same position = carousel
  if (eligible.every(ad => ad.position != null)) {
    const positionMap = new Map<number, CampaignAdEntry[]>()
    for (const ad of eligible) {
      const pos = ad.position as number
      const group = positionMap.get(pos) ?? []
      group.push(ad)
      positionMap.set(pos, group)
    }
    // Use backend position as lgPosition only; mobile uses sequential index to avoid max-lg:hidden
    const sortedEntries = [...positionMap.entries()].sort(([a], [b]) => a - b)
    return (
      <>
        {sortedEntries.map(([pos, ads], mobileIndex) => (
          <ArtworkFeedAdUnit
            key={pos}
            ad={ads[0]}
            position={mobileIndex}
            lgPosition={pos}
            entries={ads.length > 1 ? ads : undefined}
          />
        ))}
      </>
    )
  }

  // Original behavior: no position field → use calculateFeedAdPosition
  const feedCampaignWithPosition = calculateFeedAdPosition(
    feedCampaign,
    (ad: CampaignAdEntry) =>
      checkAdUnitConditionWithMembership(ad, subscription),
  )
  return (
    <>
      {feedCampaignWithPosition
        .slice(0, 4)
        .map(({ ad, position, lgPosition }) => (
          <ArtworkFeedAdUnit
            key={`${ad.trackingId}-${position}-${lgPosition}`}
            ad={ad}
            position={position}
            lgPosition={lgPosition}
          />
        ))}
    </>
  )
}

export function insertCampaignAdUnitInGrid(
  elements: (JSX.Element | null)[],
  opts?: {
    feedCampaign: CampaignAdEntry[]
  },
) {
  return [
    // Insert one at first row, first col
    <ArtworkFeedCampaignAdUnits
      feedCampaign={opts?.feedCampaign || []}
      key="feed-ads"
    />,
    ...elements,
  ]
}

export function insertAdUnitsIntoGridPerRow(
  elements: (JSX.Element | null)[],
  opts?: { gridItemType: string },
) {
  // TODO: implement when in-house ads are ready
  return elements
}
